import { ref, computed, watch } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { cartService } from '@/services/cartService'
import { cookieService } from '@/services/cookieService'

const cartItems = ref(JSON.parse(localStorage.getItem('cart')) || [])
const isCartOpen = ref(false)
const isSyncing = ref(false)
const currentCartId = ref(null)
const serverSummary = ref(null)

// Watch for changes and persist to localStorage only if NOT authenticated
watch(cartItems, (newItems) => {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) {
    localStorage.setItem('cart', JSON.stringify(newItems))
  }
}, { deep: true })

export function useCart() {
  const { t } = useLanguage()
  const authStore = useAuthStore()

  const cartCount = computed(() => {
    return cartItems.value.reduce((total, item) => total + item.quantity, 0)
  })

  const subtotal = computed(() => {
    const total = cartItems.value.reduce((sum, item) => sum + (item.price * item.quantity), 0)
    return Math.round(total * 100) / 100
  })

  const listSubtotal = computed(() => {
    const total = cartItems.value.reduce((sum, item) => {
      const p = Number(item.price)
      const op = Number(item.oldPrice)
      const priceToUse = (op && op > p) ? op : p
      return sum + (priceToUse * item.quantity)
    }, 0)
    return Math.round(total * 100) / 100
  })

  const totalSavings = computed(() => {
    return listSubtotal.value - subtotal.value
  })

  const shipping = computed(() => {
    if (serverSummary.value !== null) {
      return serverSummary.value.shippingCost
    }
    // Fallback locale: soglia dinamica recuperata da configStore
    const configStore = useConfigStore()
    return subtotal.value >= configStore.freeShippingThreshold ? 0 : 5.99
  })

  const total = computed(() => {
    if (serverSummary.value !== null) {
      return serverSummary.value.totalAmount
    }
    return subtotal.value + shipping.value
  })

  const openCart = () => {
    isCartOpen.value = true
  }

  const closeCart = () => {
    isCartOpen.value = false
  }

  const toggleCart = () => {
    isCartOpen.value = !isCartOpen.value
  }

  const mapServerCart = (data) => {
    let items = []
    
    // 0. Scompattamento preliminare (data.data o data)
    const actualData = data?.data || data
    if (!actualData) return []

    // 1. Caso: actualData è direttamente un array
    if (Array.isArray(actualData)) {
      items = actualData
    } 
    // 2. Caso: actualData ha una proprietà items o cartItems (es: { items: [...], totalCount: 1 })
    else if (actualData.items || actualData.cartItems) {
      items = actualData.items || actualData.cartItems
    }
    // 3. Caso: actualData è l'oggetto Carrello stesso
    else if (actualData.cartId || actualData.id || actualData.idCarrello) {
      items = actualData.cartItems || []
    }

    return items
      .filter(item => {
        // Filtro ultra-stringente per articoli eliminati logicamente
        // Intercettiamo ogni possibile variante restituita dalla Web API
        const deletedFlag = item.isDeleted ?? item.IsDeleted ?? item.is_deleted ?? item.Is_Deleted ?? item.deleted ?? false;
        return deletedFlag !== true && deletedFlag !== 1 && deletedFlag !== "true" && deletedFlag !== "1";
      })
      .map(item => {
      // Mapping ultra-resiliente per gestire sia la nuova struttura flat che quella nested
      // Supportiamo sia camelCase che PascalCase (per sicurezza)
      const product = item.product || {}
      
      // 1. Estrazione Prezzo e Sconto (Specifica API V2)
      // Usiamo i campi dal summary se presenti, altrimenti fallback sui campi base
      const price = Number(item.finalPrice || item.unitPrice || item.UnitPrice || 0)
      const discount = Math.round(item.displayDiscountPercentage || item.discountPercentage || item.discount || 0)
      
      // Estrazione Prezzo di Listino con fallback e ricostruzione matematica se necessario
      let oldPrice = Number(item.listPriceIncludingVat || item.listPrice || item.ListPrice || 0)
      
      // Se il prezzo di listino è mancante o inferiore al prezzo finale ma abbiamo uno sconto, lo ricostruiamo
      if ((!oldPrice || oldPrice <= price) && discount > 0) {
        oldPrice = Math.round((price / (1 - discount / 100)) * 100) / 100
      }
      
      // 2. Estrazione Nome Prodotto
      const rawName = item.productName || item.ProductName || `BILLY B - DROP ${item.variantId}`
      const name = String(rawName).toUpperCase()
      
      // 3. Estrazione Immagine (Specifica Immagini V2)
      // Il server ora invia un array ordinato filtrate per il carrello
      const imageNames = item.imageNames || item.ImageNames || []
      
      // Fallback: se imageNames è vuoto, proviamo con il vecchio campo o cerchiamo nel prodotto
      let primaryImage = imageNames.length > 0 ? imageNames[0] : (item.imageName || item.ImageName || '')
      
      if (!primaryImage && item.product?.images) {
        const fallback = item.product.images.find(img => img.isCartPreview) || item.product.images.find(img => img.isPrimary) || item.product.images[0]
        primaryImage = fallback?.imagePath || fallback?.imageUrl || ''
      }

      const size = item.size || item.Size || item.variant?.size || ''
      const color = item.colorName || item.ColorName || item.variant?.colorName || ''
      
      // Funzione di utilità interna per trovare il primo valore non vuoto
      const getFirstValid = (...vals) => vals.find(v => v && String(v).trim() !== '') || ''

      const categoryTypeSku = getFirstValid(
        item.categoryTypeSku,
        item.CategoryTypeSku,
        item.productSku,
        item.ProductSku,
        item.product?.categoryTypeSku,
        item.product?.productSku,
        item.variant?.categoryTypeSku,
        item.variant?.productSku,
        item.sku,
        item.Sku,
        item.variant?.sku
      )
      
      const sku = getFirstValid(item.sku, item.Sku, item.variant?.sku, categoryTypeSku)

      return {
        cartItemId: item.cartItemId || item.CartItemId,
        cartId: item.cartId || item.CartId || item.idCarrello || item.IdCarrello, 
        id: item.productId || item.ProductId,
        variantId: item.variantId || item.VariantId,
        name: name,
        price: price,
        oldPrice: oldPrice,
        discount: discount,
        image: primaryImage, // Immagine principale per compatibilità immediata
        imageNames: imageNames, // Array completo per utilizzi futuri (es. hover)
        color: color,
        size: size,
        sku: sku,
        categoryTypeSku: categoryTypeSku,
        categoryId: item.categoryId || item.CategoryId || item.product?.categoryId || 1,
        categoryTypeId: item.categoryTypeId || item.CategoryTypeId || item.product?.categoryTypeId || 1,
        quantity: Number(item.quantity || item.Quantity || 0)
      }
    })
  }

  // Funzione per assicurare che tutti i prodotti dello stesso tipo abbiano lo stesso SKU
  const propagateSkus = () => {
    if (cartItems.value.length <= 1) return

    const skuMap = new Map()
    // Raccogliamo tutti gli SKU validi per ogni productId
    cartItems.value.forEach(item => {
      const validSku = item.categoryTypeSku || item.sku
      if (item.id && validSku && String(validSku).trim() !== '') {
        skuMap.set(item.id, validSku)
      }
    })
    
    // Applichiamo lo SKU trovato a tutti gli articoli con lo stesso productId che ne sono privi
    if (skuMap.size > 0) {
      let changed = false
      const newItems = cartItems.value.map(item => {
        if (item.id && skuMap.has(item.id)) {
          const sharedSku = skuMap.get(item.id)
          const currentSku = item.categoryTypeSku || item.sku
          if (!currentSku || String(currentSku).trim() === '') {
            changed = true
            return { 
              ...item, 
              categoryTypeSku: sharedSku,
              sku: item.sku || sharedSku
            }
          }
        }
        return item
      })
      
      if (changed) {
        cartItems.value = newItems
      }
    }
  }

  const fetchServerCart = async () => {
    if (!authStore.isAuthenticated) return
    
    try {
      isSyncing.value = true
      
      // Recuperiamo anche i dettagli per avere il cartId
      const cartDetails = await cartService.getCartDetails()
      const actualCartData = cartDetails?.data || cartDetails
      if (actualCartData && (actualCartData.cartId || actualCartData.id)) {
        currentCartId.value = actualCartData.cartId || actualCartData.id
      }

      // 1. Recuperiamo gli articoli standard
      const standardItemsResponse = await cartService.getCartItems()
      let standardItems = mapServerCart(standardItemsResponse)
      
      // FILTRO DI SICUREZZA: Se abbiamo un cartId attivo, teniamo solo gli articoli che gli appartengono
      if (currentCartId.value) {
        standardItems = standardItems.filter(item => 
          !item.cartId || item.cartId === currentCartId.value
        )
      }

      // 2. Tentativo di arricchimento tramite Checkout/summary
      try {
        const { checkoutService } = await import('@/services/checkoutService')
        const enrichedData = await checkoutService.getCheckoutSummary()
        const actualSummaryData = enrichedData?.data || enrichedData

        if (actualSummaryData && (actualSummaryData.items || actualSummaryData.cartItems)) {
          serverSummary.value = actualSummaryData
          const summaryItems = mapServerCart(actualSummaryData)

          cartItems.value = standardItems.map(item => {
            const enrichedItem = summaryItems.find(si => si.variantId === item.variantId || (item.cartItemId && si.cartItemId === item.cartItemId))
            if (enrichedItem) {
              return {
                ...item,
                name: enrichedItem.name || item.name,
                price: enrichedItem.price || item.price,
                oldPrice: enrichedItem.oldPrice || item.oldPrice,
                discount: enrichedItem.discount || item.discount,
                image: enrichedItem.image || item.image,
                imageNames: enrichedItem.imageNames || item.imageNames || [],
                size: enrichedItem.size || item.size,
                color: enrichedItem.color || item.color,
                categoryTypeSku: enrichedItem.categoryTypeSku || item.categoryTypeSku,
                sku: enrichedItem.sku || item.sku
              }
            }
            return item
          })
          propagateSkus()
          return
        }
      } catch (e) {
        const errorCode = e?.response?.data?.code
        if (errorCode === 'CHECKOUT_EMPTY_CART') {
          serverSummary.value = null
          cartItems.value = []
          return
        }
        // Fallback silenzioso per altri errori
      }

      // Fallback se il summary fallisce o è vuoto
      cartItems.value = standardItems
      propagateSkus()
      
    } catch (error) {
      console.error('[USECART] Error fetching server cart:', error)
      throw error
    } finally {
      isSyncing.value = false
    }
  }

  const syncCartWithServer = async () => {
    if (!authStore.isAuthenticated) return

    try {
      isSyncing.value = true
      const itemsToSync = cartItems.value.map(item => ({
        variantId: item.variantId,
        quantity: item.quantity
      })).filter(item => item.variantId)

      await cartService.sync(itemsToSync)
      
      localStorage.removeItem('cart')
      await fetchServerCart()
      
      return true
    } catch (error) {
      console.error('[USECART] Error syncing cart:', error)
      return false
    } finally {
      isSyncing.value = false
    }
  }

  const addToCart = async (product, options = {}) => {
    const { color, size, quantity = 1, image, variantId } = options
    
    let finalVariantId = variantId
    if (!finalVariantId && product.variants) {
      const variant = product.variants.find(v => v.colorName === color && v.size === size)
      finalVariantId = variant?.variantId || variant?.productVariantId
    }

    if (authStore.isAuthenticated) {
      if (!finalVariantId) {
        addToLocalStorage(product, options, finalVariantId)
        return
      }

      try {
        isSyncing.value = true
        
        await cartService.addItem(0, Number(finalVariantId), Number(quantity))
        await fetchServerCart()
        openCart()

        // Meta Pixel: AddToCart
        cookieService.trackPixelEvent('AddToCart', {
          content_ids: [String(product.categoryTypeSku || product.id)],
          content_type: 'product',
          value: product.price * quantity,
          currency: 'EUR'
        })

        // TikTok Pixel: AddToCart
        cookieService.trackTikTokEvent('AddToCart', {
          content_id: String(product.categoryTypeSku || product.id),
          contents: [{
            content_id: String(product.categoryTypeSku || product.id),
            content_type: 'product',
            content_name: product.name,
            price: product.price,
            quantity: quantity
          }],
          content_type: 'product',
          value: product.price * quantity,
          currency: 'EUR'
        })

        // GA4: add_to_cart
        cookieService.trackGA4EcommerceEvent('add_to_cart', {
          currency: 'EUR',
          value: product.price * quantity,
          items: [{
            item_id: String(product.categoryTypeSku || product.id),
            item_name: product.name,
            price: product.price,
            quantity: quantity
          }]
        })
      } catch (error) {
        console.error('[USECART] Error adding to server cart:', error)
        throw error
      } finally {
        isSyncing.value = false
      }
    } else {
      addToLocalStorage(product, options, finalVariantId)
      
      // Meta Pixel: AddToCart (Guest)
      cookieService.trackPixelEvent('AddToCart', {
        content_ids: [String(product.categoryTypeSku || product.id)],
        content_type: 'product',
        value: product.price * quantity,
        currency: 'EUR'
      })

      // TikTok Pixel: AddToCart (Guest)
      cookieService.trackTikTokEvent('AddToCart', {
        content_id: String(product.categoryTypeSku || product.id),
        contents: [{
          content_id: String(product.categoryTypeSku || product.id),
          content_type: 'product',
          content_name: product.name,
          price: product.price,
          quantity: quantity
        }],
        content_type: 'product',
        value: product.price * quantity,
        currency: 'EUR'
      })

      // GA4: add_to_cart (Guest)
      cookieService.trackGA4EcommerceEvent('add_to_cart', {
        currency: 'EUR',
        value: product.price * quantity,
        items: [{
          item_id: String(product.categoryTypeSku || product.id),
          item_name: product.name,
          price: product.price,
          quantity: quantity
        }]
      })
    }
  }

  const addToLocalStorage = (product, options, finalVariantId) => {
    const { color, size, quantity = 1, image } = options

    // Trova la variante selezionata per estrarre lo SKU corretto
    let variantSku = ''
    if (finalVariantId && product.variants) {
      const variant = product.variants.find(v => v.variantId === finalVariantId || v.productVariantId === finalVariantId)
      if (variant) {
        variantSku = variant.sku
      }
    } else if (product.sku || product.productSku || product.categoryTypeSku) { 
      variantSku = product.sku || product.productSku || product.categoryTypeSku
    }

    const existingItem = cartItems.value.find(item => 
      (finalVariantId && item.variantId === finalVariantId) ||
      (!finalVariantId && item.id === product.id && item.color === color && item.size === size)
    )

    if (existingItem) {
      existingItem.quantity += quantity
    } else {
      // Preferiamo l'immagine marcata isCartPreview, poi la quarta disponibile come fallback
      const cartPreviewImage = product.cartPreviewImage?.imageUrl || product.cartPreviewImage?.imageName
      const fallbackImage = cartPreviewImage ||
                          (product.colors && product.colors[0]?.images && product.colors[0]?.images[3]?.imageName) || 
                          (product.colors && product.colors[0]?.frontImageFileName)
                          
      cartItems.value.push({
        id: product.id,
        variantId: finalVariantId,
        name: product.name,
        price: product.price,
        oldPrice: product.oldPrice,
        discount: product.discount,
        image: image || product.image || fallbackImage,
        color: color,
        size: size,
        sku: variantSku,
        categoryTypeSku: product.categoryTypeSku || product.productSku || variantSku || '',
        quantity: quantity
      })
    }
    
    propagateSkus()
    openCart()
  }

  const removeFromCart = async (itemId, color, size, variantId, cartItemId) => {
    if (authStore.isAuthenticated && (cartItemId || variantId)) {
      try {
        isSyncing.value = true
        const idToRemove = cartItemId || cartItems.value.find(i => i.variantId === variantId)?.cartItemId
        if (idToRemove) {
          await cartService.removeItem(idToRemove)
          await fetchServerCart()
        }
      } catch (error) {
        console.error('[USECART] Error removing from server cart:', error)
        throw error
      } finally {
        isSyncing.value = false
      }
    } else {
      const index = cartItems.value.findIndex(item => 
        (variantId && item.variantId === variantId) ||
        (!variantId && item.id === itemId && item.color === color && item.size === size)
      )
      if (index !== -1) {
        cartItems.value.splice(index, 1)
      }
    }
  }

  const updateQuantity = async (itemId, color, size, quantity, variantId, cartItemId) => {
    if (quantity < 1) {
      await removeFromCart(itemId, color, size, variantId, cartItemId)
      return
    }

    if (authStore.isAuthenticated && (cartItemId || variantId)) {
      try {
        isSyncing.value = true
        const idToUpdate = cartItemId || cartItems.value.find(i => i.variantId === variantId)?.cartItemId
        if (idToUpdate) {
          await cartService.updateQuantity(idToUpdate, quantity)
          await fetchServerCart()
        }
      } catch (error) {
        console.error('[USECART] Error updating server cart quantity:', error)
        throw error
      } finally {
        isSyncing.value = false
      }
    } else {
      const item = cartItems.value.find(item => 
        (variantId && item.variantId === variantId) ||
        (!variantId && item.id === itemId && item.color === color && item.size === size)
      )
      if (item) {
        item.quantity = quantity
      }
    }
  }

  const clearCart = async () => {
    if (authStore.isAuthenticated) {
      try {
        isSyncing.value = true
        
        // 1. Prepariamo la lista degli articoli correnti per il fallback
        const itemsToRemove = [...cartItems.value]
        
        // 2. Cerchiamo l'ID del carrello in modo ultra-resiliente
        const cartDetails = await cartService.getCartDetails()
        const d = Array.isArray(cartDetails?.data) ? cartDetails.data[0] : (cartDetails?.data || cartDetails)
        let cartIdToClear = d?.cartId || d?.CartId || d?.idCarrello || d?.IdCarrello || d?.id || d?.id_carrello
        
        if (!cartIdToClear && itemsToRemove.length > 0) {
          cartIdToClear = itemsToRemove[0].cartId || itemsToRemove[0].CartId || itemsToRemove[0].idCarrello
        }

        let globalClearSuccess = false
        if (cartIdToClear) {
          try {
            await cartService.clearCart(cartIdToClear)
            globalClearSuccess = true
          } catch (e) {
            // Fallback silenzioso
          }
        }

        // 3. FALLBACK: Se la delete globale fallisce o vogliamo essere sicuri al 100%, 
        // rimuoviamo gli articoli uno ad uno (questo forza isDeleted su ogni riga del DB)
        if (!globalClearSuccess || itemsToRemove.length > 0) {
          const removePromises = itemsToRemove
            .filter(item => item.cartItemId)
            .map(item => cartService.removeItem(item.cartItemId).catch(() => {}))
          
          await Promise.all(removePromises)
        }
        
        // 4. Reset totale stato locale
        cartItems.value = []
        currentCartId.value = null
        localStorage.removeItem('cart')

      } catch (error) {
        console.error('[USECART] Errore durante lo svuotamento del carrello:', error)
        throw error
      } finally {
        // Rinfreschiamo dal server per confermare che sia tutto vuoto
        await fetchServerCart()
        isSyncing.value = false
      }
    } else {
      cartItems.value = []
      localStorage.removeItem('cart')
    }
  }

  const resetCart = () => {
    cartItems.value = []
    localStorage.removeItem('cart')
  }

  return {
    cartItems,
    isCartOpen,
    isSyncing,
    cartCount,
    subtotal,
    listSubtotal,
    totalSavings,
    shipping,
    total,
    serverSummary,
    openCart,
    closeCart,
    toggleCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    resetCart,
    syncCartWithServer,
    fetchServerCart
  }
}
